import React, { useState } from 'react';
import { Link2, Check, Share2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareButtons — social share row for a product / listing.
 * Props:
 *  - url (required): fully-qualified share URL
 *  - title (required): product title / share message subject
 *  - description (optional): short blurb (used by LinkedIn summary + email body)
 */
export default function ShareButtons({ url, title, description = '' }) {
  const [copied, setCopied] = useState(false);

  const shareUrl = encodeURIComponent(url);
  const shareTitle = encodeURIComponent(title);
  const shareText = encodeURIComponent(
    description ? `${title} — ${description}` : title
  );

  const whatsappHref = `https://wa.me/?text=${shareText}%20${shareUrl}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`;
  const xHref = `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}`;
  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`;
  const emailHref = `mailto:?subject=${shareTitle}&body=${shareText}%0A%0A${shareUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Unable to copy link');
    }
  };

  const handleNativeShare = async () => {
    if (!navigator.share) return;
    try {
      await navigator.share({ title, text: description || title, url });
    } catch (err) {
      // AbortError = user cancelled share sheet; any other err is benign for sharing flow.
      if (err?.name !== 'AbortError') {
        console.debug('navigator.share failed:', err?.message || err);
      }
    }
  };

  const iconBtn =
    'inline-flex items-center justify-center w-10 h-10 rounded-full border border-border bg-white hover:bg-muted transition-colors';

  return (
    <div className="mt-6" data-testid="share-buttons">
      <p className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
        <Share2 className="h-4 w-4" />
        Share this listing
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className={iconBtn}
          title="Share on WhatsApp"
          aria-label="Share on WhatsApp"
          data-testid="share-whatsapp"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#25D366" aria-hidden="true">
            <path d="M20.52 3.48A11.9 11.9 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 5.98L0 24l6.2-1.62A12 12 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52ZM12 22a10 10 0 0 1-5.1-1.4l-.37-.22-3.68.96.98-3.58-.24-.37A10 10 0 1 1 22 12c0 5.52-4.48 10-10 10Zm5.48-7.48c-.3-.15-1.77-.87-2.04-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.5-.89-.79-1.49-1.77-1.67-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.38-.02-.53-.07-.15-.67-1.62-.92-2.22-.24-.58-.5-.5-.67-.51l-.58-.01c-.2 0-.53.07-.8.38-.27.3-1.05 1.02-1.05 2.5 0 1.48 1.07 2.91 1.22 3.11.15.2 2.12 3.23 5.13 4.53.72.31 1.28.49 1.72.63.72.23 1.37.2 1.88.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.08-.12-.27-.2-.57-.35Z"/>
          </svg>
        </a>

        <a
          href={facebookHref}
          target="_blank"
          rel="noopener noreferrer"
          className={iconBtn}
          title="Share on Facebook"
          aria-label="Share on Facebook"
          data-testid="share-facebook"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#1877F2" aria-hidden="true">
            <path d="M24 12a12 12 0 1 0-13.88 11.85v-8.38H7.08V12h3.04V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.23 2.68.23v2.95h-1.51c-1.49 0-1.95.92-1.95 1.87V12h3.32l-.53 3.47h-2.79v8.38A12 12 0 0 0 24 12Z"/>
          </svg>
        </a>

        <a
          href={xHref}
          target="_blank"
          rel="noopener noreferrer"
          className={iconBtn}
          title="Share on X"
          aria-label="Share on X"
          data-testid="share-x"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#0F1419" aria-hidden="true">
            <path d="M18.244 2H21.5l-7.54 8.61L23 22h-6.95l-5.23-6.84L4.8 22H1.54l8.06-9.21L1 2h7.12l4.72 6.24L18.244 2Zm-1.22 18h1.82L7.06 4H5.14l11.884 16Z"/>
          </svg>
        </a>

        <a
          href={linkedinHref}
          target="_blank"
          rel="noopener noreferrer"
          className={iconBtn}
          title="Share on LinkedIn"
          aria-label="Share on LinkedIn"
          data-testid="share-linkedin"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
            <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05a3.75 3.75 0 0 1 3.37-1.85c3.6 0 4.27 2.37 4.27 5.45v6.29ZM5.34 7.44a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14ZM7.12 20.45H3.56V9h3.56v11.45ZM22.23 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.46c.98 0 1.77-.77 1.77-1.72V1.72C24 .77 23.21 0 22.23 0Z"/>
          </svg>
        </a>

        <a
          href={emailHref}
          className={iconBtn}
          title="Share via Email"
          aria-label="Share via Email"
          data-testid="share-email"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </a>

        <button
          type="button"
          onClick={handleCopy}
          className={iconBtn}
          title="Copy link"
          aria-label="Copy link"
          data-testid="share-copy"
        >
          {copied ? (
            <Check className="h-5 w-5 text-emerald-600" />
          ) : (
            <Link2 className="h-5 w-5 text-muted-foreground" />
          )}
        </button>

        {typeof navigator !== 'undefined' && navigator.share && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="ml-1 inline-flex items-center gap-2 px-3 h-10 rounded-full border border-border bg-white hover:bg-muted text-sm transition-colors"
            data-testid="share-native"
          >
            <Share2 className="h-4 w-4" />
            More…
          </button>
        )}
      </div>
    </div>
  );
}
